import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import customFetch, { checkForUnauthorizedResponse } from "../../utils/axios";
import toast from "react-hot-toast";
import {
  addItemToLocalStorage,
  addStepsToLocalStorage,
  addUserToLocalStorage,
  getStepsFromLocalStorage,
  getUserFromLocalStorage,
  removeUserFromLocalStorage,
} from "../../utils/localstorage";
import nprogress from "nprogress";
import { hasPermission } from "../../utils/authHelpers";
import { useDispatch } from "react-redux";

const initialState = {
  isLoading: false,
  user: getUserFromLocalStorage(),
  errMsg: null,
  changePassword: false,
  passwordChanged: false,
  providerCredit: null,
  transactions: [],
  userSignUp: null,
  allState: [],
  stateLga: [],
  userImgLink: null,
  subnitBasicInfo: "",
  steps: getStepsFromLocalStorage() || 0,
  navTitle: "",
  providerBenefit: [],
  benefitCategory: [],
  uploadingCsv: false,
  csvSucces: false,
  submitClientsManuallyLoading: false,
  submitClientsManuallySuccess: false,
  client: {},
  passDetails: {},
  providerBranch: [],
  providerBranches: [],
  providerCategory: [],
  providerAccountBranches: [],
  providerImage: null,
  transferSuccess: false,
  revenueData: {},
  providerData: {},
  loadingReceipt: false,
  loadingOnlineToggle: false,
  loadingExpiry: false,
  notificationPricing: {},
  teamMembers: [],
  userClaims: [],
  providerRoles: [],
  invitedUserDetails: {},
  providerContract: [],
  providerContracts: [],
  resellers: [],
};

export const registerUser = createAsyncThunk(
  "user/registerUser",
  async (user, thunkAPI) => {
    localStorage.setItem("businessMail", JSON.stringify(user.businessEmail));
    try {
      const resp = await customFetch.post("auth/signup/provider", user);
      return resp?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async (user, thunkAPI) => {
    try {
      const resp = await customFetch.post("auth/signin/provider", user);
      return resp?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);
export const loginInvitedUser = createAsyncThunk(
  "user/loginInvitedUser",
  async (user, thunkAPI) => {
    try {
      const resp = await customFetch.post("auth/invited/provider/create", user);
      const userString = localStorage.getItem("inviteData");
      let inviteData = userString ? JSON.parse(userString) : {};
      inviteData.token = user.token;
      localStorage.setItem("inviteData", JSON.stringify(inviteData));
      return resp?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);
export const getUserClaims = createAsyncThunk(
  "user/getUserClaims",
  async (_, thunkAPI) => {
    try {
      const resp = await customFetch.get("provider/extract/claims");
      return resp?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);

export const setupUser = createAsyncThunk(
  "user/setupUser",
  async (user, thunkAPI) => {
    try {
      const resp = await customFetch.post("provider/password/setup", user);
      return resp?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);

export const getProviderBranch = createAsyncThunk(
  "user/getProviderBranch",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    try {
      const resp = await customFetch.get("provider/branches");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderCategory = createAsyncThunk(
  "user/getProviderCategory",
  async (_, thunkAPI) => {
    try {
      const resp = await customFetch.get("auth/provider/categories/all");
      return resp?.data?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getProviderAccountBranch = createAsyncThunk(
  "user/getProviderAccountBranch",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get("provider/account/branches");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getProviderDetails = createAsyncThunk(
  "user/getProviderDetails",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get("provider");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getProviderBranchByCategory = createAsyncThunk(
  "user/getProviderBranchByCategory",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get("branches/category/GYM");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderSubscribersById = createAsyncThunk(
  "user/getProviderSubscribersById",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get(
        `provider/clients/${payload?.id}/${payload?.clientType}`
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getProviderCredit = createAsyncThunk(
  "user/creditProvider",
  async (id, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "BRANCH_MANAGER")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get(`provider/credit`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderCreditTransactions = createAsyncThunk(
  "user/creditProviderTransacions",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "BRANCH_MANAGER")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get(
        `provider/credit/transactions?startDate=${
          payload?.startDate ?? ""
        }&endDate=${payload?.endDate ?? ""}`
      );
      return resp?.data?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const fetchAllStates = createAsyncThunk(
  "user/allStates",
  async (_, thunkAPI) => {
    try {
      const response = await customFetch.get("benefit/states");
      const findState = response?.data;
      return findState;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const fetchImageLink = createAsyncThunk(
  "user/fetchImageLink",
  async (_, thunkAPI) => {
    try {
      const response = await customFetch.get(`provider/signed-url/png`);
      console.log(response, "response");
      return response?.data;
    } catch (error) {
      console.error("Error fetching image link:", error);
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const fetchProviderBenefit = createAsyncThunk(
  "user/fetchProviderBenefit",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;
    const key = user?.user?.data?.providerId;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    const getFromLocalStorage = () => {
      try {
        const value = localStorage.getItem(`${key}branch`);

        if (value === null || value === undefined || value === "undefined") {
          return null; // Return null if the key doesn't exist
        }
        return JSON.parse(value); // Parse the JSON value
      } catch (error) {
        console.error("Error parsing JSON from localStorage", error);
        return null; // Return null if parsing fails
      }
    };
    const { id } = getFromLocalStorage();
    try {
      const response = await customFetch.get(`provider/benefit?branchId=${id}`);
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const deleteProviderBenefit = createAsyncThunk(
  "user/deleteProviderBenefit",
  async (id, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "BRANCH_MANAGER")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.delete(`provider/benefit/${id}`);
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const fetchAllLgas = createAsyncThunk(
  "user/allLgas",
  async (state, thunkAPI) => {
    try {
      const response = await customFetch.get(`benefit/states/${state}/lgas`);
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const fetchProviderCategory = createAsyncThunk(
  "user/fetchProviderCategory",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.get(`auth/provider/categories`);
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);
export const resetProviderPassword = createAsyncThunk(
  "user/resetProviderPassword",
  async (user, thunkAPI) => {
    // const userRole = user?.user?.data?.roleName;

    // if (!hasPermission(userRole, "ADMIN")) {
    //   console.log("Permission denied");
    //   return;
    // }
    try {
      const resp = await customFetch.post(
        "auth/provider/password/change",
        user
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const acceptResellerInvitation = createAsyncThunk(
  "user/acceptResellerInvitation",
  async (user, thunkAPI) => {
    try {
      const resp = await customFetch.post(
        "provider/reseller/invitation/accept",
        user,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const changeOldPassword = createAsyncThunk(
  "user/changeOldPassword",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.put("provider/password/change", payload);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const submitProviderInfo = createAsyncThunk(
  "user/submitProviderInfo",
  async (info, thunkAPI) => {
    try {
      const resp = await customFetch.post("provider/info", info);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const addServicePricing = createAsyncThunk(
  "user/addServicePricing",
  async (payload, thunkAPI) => {
    try {
      const resp = await customFetch.post(
        "provider/contract/services",
        payload
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const acceptContract = createAsyncThunk(
  "user/acceptContract",
  async (payload, thunkAPI) => {
    try {
      const resp = await customFetch.put("provider/contract/accept", payload);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const editContract = createAsyncThunk(
  "user/editContract",
  async (payload, thunkAPI) => {
    try {
      const resp = await customFetch.put("provider/contract/edit", payload);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const submitPassCode = createAsyncThunk(
  "user/submitPassCode",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post(
        "provider/flance-pass/visit/log",
        info
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const submitPlanDetails = createAsyncThunk(
  "user/submitPlanDetails",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post("provider/benefit", info);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const submitClientManually = createAsyncThunk(
  "user/submitClientManually",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post("provider/clients", info);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const updateProviderClients = createAsyncThunk(
  "user/updateProviderClients",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post("provider/client/change", info, {
        headers: {
          "FL-IDEMPOTENT-KEY": `${info?.providerId}${info?.clientId}${info?.oldBenefitId}${info?.newBenefitId}`,
        },
      });
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const addContactPerson = createAsyncThunk(
  "user/addContactPerson",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post("provider/contactPerson", info);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getContactPerson = createAsyncThunk(
  "user/getContactPerson",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get("provider/contactPerson");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const updateProviderBenefits = createAsyncThunk(
  "user/updateProviderBenefits",
  async (info, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.put(`provider/benefit/${info?.id}`, {
        priceOptions: info?.price,
        benefitName: info?.benefitName,
      });
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const updateProviderLogo = createAsyncThunk(
  "user/updateProviderLogo",
  async (imgLink, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post(`provider/image?imageUrl=${imgLink}`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const submitProviderClientsWithCSV = createAsyncThunk(
  "user/submitProviderClientsWithCSV",
  async (file, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post("provider/clients/upload", file);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderImage = createAsyncThunk(
  "user/getProviderImage",
  async (providerId, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post(`provider/info/${providerId}`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const madeTransferSuccess = createAsyncThunk(
  "user/madeTransferSuccess",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.post(`provider/notify/admin`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getRevenueData = createAsyncThunk(
  "user/revenueData",
  async (timePeriod, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "BRANCH_MANAGER")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.get(
        `provider/revenue/data?timeFilter=${timePeriod}`
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const toggleOnlinePayment = createAsyncThunk(
  "user/toggleOnlinePayment",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.put(`provider/online/payments/toggle`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const togglePaymentRecipt = createAsyncThunk(
  "user/togglePaymentRecipt",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.put(`provider/receipt/toggle`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const toggleRemindersForAutoRenewal = createAsyncThunk(
  "user/toggleRemindersForAutoRenewal",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "ADMIN")) {
      console.log("Permission denied");
      return;
    }
    try {
      const resp = await customFetch.put(
        `provider/clients/renew/email/days?days=${payload.join(",")}`
      );
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getNotificationPricing = createAsyncThunk(
  "user/getNotificationPricing",
  async (state, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.get("provider/notifications/pricing");
      return response?.data?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getTeamMembers = createAsyncThunk(
  "user/getTeamMembers",
  async (_, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.get("provider/team/members");
      return response?.data?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const createTeamMember = createAsyncThunk(
  "user/createTeamMember",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.post("provider/role/assign", payload);
      await thunkAPI.dispatch(getTeamMembers());
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const editTeamMember = createAsyncThunk(
  "user/editTeamMember",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.put(
        `provider/role/update/${payload?.id}`,

        {
          role: payload?.role,
          branchIds: payload?.branchIds,
        }
      );
      await thunkAPI.dispatch(getTeamMembers());
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const deleteTeamMember = createAsyncThunk(
  "user/deleteTeamMember",
  async (payload, thunkAPI) => {
    const { user } = thunkAPI.getState();
    const userRole = user?.user?.data?.roleName;

    if (!hasPermission(userRole, "OPERATOR")) {
      console.log("Permission denied");
      return;
    }
    try {
      const response = await customFetch.put(
        `provider/role/revoke/${payload?.id}`
      );
      await thunkAPI.dispatch(getTeamMembers());
      return response?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

export const getProviderRoles = createAsyncThunk(
  "user/getProviderRoles",
  async (_, thunkAPI) => {
    try {
      const resp = await customFetch.get("provider/roles");
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderProviderContracts = createAsyncThunk(
  "user/getProviderProviderContracts",
  async (id, thunkAPI) => {
    try {
      const resp = await customFetch.get(`provider/pending/contract/${id}`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getProviderContracts = createAsyncThunk(
  "user/getProviderContracts",
  async (_, thunkAPI) => {
    try {
      const resp = await customFetch.get(`provider/pending/contracts`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);
export const getResellerDetails = createAsyncThunk(
  "user/getResellerDetails",
  async (id, thunkAPI) => {
    try {
      const resp = await customFetch.get(`provider/partners`);
      return resp?.data;
    } catch (error) {
      return checkForUnauthorizedResponse(error, thunkAPI);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = null;
      removeUserFromLocalStorage();
      localStorage.removeItem("inviteData");
      localStorage.removeItem("currentStep");
      window.location.href = "/auth/login";
    },
    returnChangePassword: (state) => {
      state.changePassword = false;
    },
    increaseSteps: (state) => {
      state.steps = 0;
      addStepsToLocalStorage(state.steps);
    },
    changeNavtitle: (state, { payload }) => {
      state.navTitle = payload;
    },
    resetManaulClientUpload: (state, { payload }) => {
      state.submitClientsManuallySuccess = false;
    },
    setInvitedUserDetails: (state, { payload }) => {
      state.invitedUserDetails = payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.userSignUp = payload;
        toast.success("You are successfully signned up");
        state.user = payload;
        addUserToLocalStorage(payload);
        console.log(payload);
        localStorage.removeItem("loginData");
        localStorage.removeItem("currentStep");
      })
      .addCase(registerUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
        toast.error(payload?.message);
        state.changePassword = false;
        localStorage.removeItem("loginData");
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success("You are successfully logged in");
        state.user = payload;
        addUserToLocalStorage(payload);
        localStorage.removeItem("loginData");
        localStorage.removeItem("notificationShown");
        localStorage.removeItem("inviteData");
        state.changePassword = false;
      })
      .addCase(loginUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        if (payload?.message === "Kindly Proceed To Change Your Password") {
          state.changePassword = true;
          toast.loading(payload?.message);
          addItemToLocalStorage("loginData", { id: payload?.data });
        } else {
          state.changePassword = false;
          localStorage.removeItem("loginData");
          toast.error(payload?.message);
        }
        state.errMsg = payload;
      })
      .addCase(loginInvitedUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginInvitedUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success("You are successfully logged in");
        state.user = payload;
      })
      .addCase(loginInvitedUser.rejected, (state, { payload }) => {
        state.isLoading = false;

        state.errMsg = payload;
      })
      .addCase(getProviderBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setupUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(setupUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success("You have successfully setup your account");
      })
      .addCase(setupUser.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload?.message);
        state.errMsg = payload;
      })
      .addCase(getUserClaims.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserClaims.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.userClaims = payload;
      })
      .addCase(getUserClaims.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderBranch.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerBranch = payload;
      })
      .addCase(getProviderBranch.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderAccountBranch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderAccountBranch.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerAccountBranches = payload;
      })
      .addCase(getProviderAccountBranch.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderBranchByCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderBranchByCategory.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerBranches = payload;
      })
      .addCase(getProviderBranchByCategory.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderCategory.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerCategory = payload;
      })
      .addCase(getProviderCategory.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderSubscribersById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderSubscribersById.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.client = payload;
      })
      .addCase(getProviderSubscribersById.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.error(payload?.message);
      })
      .addCase(fetchProviderCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProviderCategory.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.benefitCategory = payload;
      })
      .addCase(fetchProviderCategory.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(fetchImageLink.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchImageLink.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.userImgLink = payload;
      })
      .addCase(fetchImageLink.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(fetchAllStates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllStates.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.allState = payload;
      })
      .addCase(fetchAllStates.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(fetchAllLgas.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllLgas.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.stateLga = payload;
      })
      .addCase(fetchAllLgas.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(resetProviderPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetProviderPassword.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.passwordChanged = true;
        localStorage.removeItem("loginData");
        toast.success("Password changed successfully");
      })
      .addCase(resetProviderPassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload?.message);
      })
      .addCase(acceptResellerInvitation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(acceptResellerInvitation.fulfilled, (state, { payload }) => {
        state.isLoading = false;
      })
      .addCase(acceptResellerInvitation.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload?.message);
      })
      .addCase(getProviderCredit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderCredit.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerCredit = payload;
      })
      .addCase(getProviderCredit.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload?.message);
      })
      .addCase(submitProviderInfo.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(submitProviderInfo.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
        state.steps = 1;
        addStepsToLocalStorage(state.steps);
        nprogress.done();
      })
      .addCase(submitProviderInfo.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(addServicePricing.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(addServicePricing.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
        localStorage.removeItem("providerPricing");
        nprogress.done();
      })
      .addCase(addServicePricing.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(acceptContract.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(acceptContract.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
        localStorage.removeItem("providerInvitationPricing");
        nprogress.done();
      })
      .addCase(acceptContract.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(editContract.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(editContract.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
        localStorage.removeItem("providerInvitationPricing");
        nprogress.done();
      })
      .addCase(editContract.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(submitPlanDetails.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(submitPlanDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
        state.steps = 2;
        addStepsToLocalStorage(state.steps);
        nprogress.done();
      })
      .addCase(submitPlanDetails.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
        nprogress.done();
      })
      .addCase(submitClientManually.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        state.submitClientsManuallySuccess = false;
        nprogress.start();
      })
      .addCase(submitClientManually.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        state.submitClientsManuallySuccess = true;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(submitClientManually.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        state.submitClientsManuallySuccess = false;
        payload && toast.error(payload);
        nprogress.done();
      })
      .addCase(updateProviderClients.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        nprogress.start();
      })
      .addCase(updateProviderClients.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(updateProviderClients.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload && toast.error(payload);
        nprogress.done();
      })
      .addCase(addContactPerson.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        nprogress.start();
      })
      .addCase(addContactPerson.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(addContactPerson.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload && toast.error(payload);
        nprogress.done();
      })
      .addCase(getContactPerson.pending, (state) => {
        state.submitClientsManuallyLoading = true;
      })
      .addCase(getContactPerson.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        state.contactPerson = payload;
      })
      .addCase(getContactPerson.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload && toast.error(payload?.message);
      })
      .addCase(submitPassCode.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        nprogress.start();
      })
      .addCase(submitPassCode.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        state.passDetails = payload;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(submitPassCode.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload && toast.error(payload);
        nprogress.done();
      })
      .addCase(updateProviderBenefits.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        nprogress.start();
      })
      .addCase(updateProviderBenefits.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(updateProviderBenefits.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload?.message && toast.error(payload?.message);
        nprogress.done();
      })
      .addCase(fetchProviderBenefit.pending, (state) => {
        state.isLoading = true;
        nprogress.start();
      })
      .addCase(fetchProviderBenefit.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerBenefit = payload?.data;
        nprogress.done();
      })
      .addCase(fetchProviderBenefit.rejected, (state, { payload }) => {
        state.isLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(deleteProviderBenefit.pending, (state) => {
        state.submitClientsManuallyLoading = true;
        nprogress.start();
      })
      .addCase(deleteProviderBenefit.fulfilled, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        payload?.message && toast.success(payload?.message);
        nprogress.done();
      })
      .addCase(deleteProviderBenefit.rejected, (state, { payload }) => {
        state.submitClientsManuallyLoading = false;
        toast.error(payload);
        nprogress.done();
      })
      .addCase(submitProviderClientsWithCSV.pending, (state) => {
        nprogress.start();
        state.uploadingCsv = true;
        state.csvSucces = false;
      })
      .addCase(submitProviderClientsWithCSV.fulfilled, (state, { payload }) => {
        nprogress.done();
        state.uploadingCsv = false;
        state.csvSucces = true;
        payload?.message && toast.success(payload?.message);
      })
      .addCase(submitProviderClientsWithCSV.rejected, (state, { payload }) => {
        nprogress.done();
        state.uploadingCsv = false;
        state.csvSucces = false;
        toast.error(payload);
      })
      .addCase(updateProviderLogo.pending, (state) => {
        nprogress.start();
        state.uploadingCsv = true;
        state.csvSucces = false;
      })
      .addCase(updateProviderLogo.fulfilled, () => {
        toast.success("Image uploaded successfully");
      })
      .addCase(updateProviderLogo.rejected, () => {
        nprogress.done();
        toast.error("Image not uploaded");
      })
      .addCase(getProviderCreditTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        getProviderCreditTransactions.fulfilled,
        (state, { payload }) => {
          state.isLoading = false;
          state.transactions = payload;
        }
      )
      .addCase(getProviderCreditTransactions.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.error(payload?.message);
      })
      .addCase(getProviderImage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderImage.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerImage = payload;
      })
      .addCase(getProviderImage.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.error(payload?.message);
      })
      .addCase(madeTransferSuccess.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(madeTransferSuccess.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.transferSuccess = true;
        payload?.message && toast.success(payload?.message);
      })
      .addCase(madeTransferSuccess.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.error(payload?.message);
      })
      .addCase(changeOldPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changeOldPassword.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        payload?.message && toast.success(payload?.message);
      })
      .addCase(changeOldPassword.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(getRevenueData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRevenueData.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.revenueData = payload?.data;
      })
      .addCase(getRevenueData.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(getProviderDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerData = payload?.data;
      })
      .addCase(getProviderDetails.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(toggleOnlinePayment.pending, (state) => {
        state.loadingOnlineToggle = true;
      })
      .addCase(toggleOnlinePayment.fulfilled, (state, { payload }) => {
        state.loadingOnlineToggle = false;
        payload?.message && toast.success(payload?.message);
      })
      .addCase(toggleOnlinePayment.rejected, (state, { payload }) => {
        state.loadingOnlineToggle = false;
        payload && toast.error(payload);
      })
      .addCase(togglePaymentRecipt.pending, (state) => {
        state.loadingReceipt = true;
      })
      .addCase(togglePaymentRecipt.fulfilled, (state, { payload }) => {
        state.loadingReceipt = false;
        payload?.message && toast.success(payload?.message);
      })
      .addCase(togglePaymentRecipt.rejected, (state, { payload }) => {
        state.loadingReceipt = false;
        payload && toast.error(payload);
      })
      .addCase(toggleRemindersForAutoRenewal.pending, (state) => {
        state.loadingExpiry = true;
      })
      .addCase(
        toggleRemindersForAutoRenewal.fulfilled,
        (state, { payload }) => {
          state.loadingExpiry = false;
          payload?.message && toast.success(payload?.message);
        }
      )
      .addCase(toggleRemindersForAutoRenewal.rejected, (state, { payload }) => {
        state.loadingExpiry = false;
        payload && toast.error(payload);
      })
      .addCase(getNotificationPricing.pending, (state) => {
        state.loadingExpiry = true;
      })
      .addCase(getNotificationPricing.fulfilled, (state, { payload }) => {
        state.loadingExpiry = false;
        state.notificationPricing = payload;
      })
      .addCase(getNotificationPricing.rejected, (state, { payload }) => {
        state.loadingExpiry = false;
        payload && toast.error(payload);
      })
      .addCase(getTeamMembers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getTeamMembers.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.teamMembers = payload;
      })
      .addCase(getTeamMembers.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(createTeamMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTeamMember.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success(payload?.message);
      })
      .addCase(createTeamMember.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(editTeamMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(editTeamMember.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success(payload?.message);
      })
      .addCase(editTeamMember.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(deleteTeamMember.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteTeamMember.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        toast.success(payload?.message);
      })
      .addCase(deleteTeamMember.rejected, (state, { payload }) => {
        state.isLoading = false;
        payload && toast.error(payload);
      })
      .addCase(getProviderRoles.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderRoles.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerRoles = payload.data;
      })
      .addCase(getProviderRoles.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderProviderContracts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderProviderContracts.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerContract = payload.data;
      })
      .addCase(getProviderProviderContracts.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getProviderContracts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProviderContracts.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.providerContracts = payload.data;
      })
      .addCase(getProviderContracts.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      })
      .addCase(getResellerDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getResellerDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.resellers = payload.data;
      })
      .addCase(getResellerDetails.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.errMsg = payload;
      });
  },
});

export const {
  logoutUser,
  returnChangePassword,
  increaseSteps,
  changeNavtitle,
  resetManaulClientUpload,
  setInvitedUserDetails,
} = userSlice.actions;

export default userSlice.reducer;
